'use client';

import { useContext } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useHomePromotions, trackClick } from '@mro-casino/promo-react';
import { AppContext } from '@/src/context/AppContext';
import styles from './HomePromoCoupon.module.css';

/**
 * Homepage promo strip — shows whatever POP currently resolves as the
 * default/fallback coupon for the "home_feature_grid" placement. Renders
 * nothing while loading, on error, or when POP returns no promotions — this
 * is additive, never a layout placeholder.
 *
 * Guest-only, matching the legacy production homepage banner it replaces
 * (operator-confirmed 2026-09-16: the old GOATSTART welcome banner is only
 * ever shown to a logged-out visitor and disappears once they log in) — so
 * this hides as soon as `user` is set, it does not just stop fetching.
 *
 * The caller (app/page.js) gates whether this even mounts on
 * NEXT_PUBLIC_DYNAMIC_PROMOS_ENABLED, same flag used for the rest of the DAC
 * integration, so no extra fetch happens at all when the feature is off —
 * see docs/BRAND_ONBOARDING_MASTER_CHECKLIST_SR.md and
 * GOAT_ONBOARDING_STATUS_SR.md (FAZA D3) in promo-operations-platform.
 *
 * `placement` lets a future second homepage slot reuse this same component
 * against a different POP release without code changes — see the matching
 * comment in app/api/promotions/home/route.js. Only "home_feature_grid" is
 * actually configured in POP for Goat today.
 */
export default function HomePromoCoupon({ placement = 'home_feature_grid' }) {
  const { user } = useContext(AppContext);
  const apiPath = `/api/promotions/home?placement=${encodeURIComponent(placement)}`;
  const { data, loading, error } = useHomePromotions({ apiPath });

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (user || loading || error || !data?.length) return null;

  // The current/default coupon for this placement — POP already resolved
  // "current" (default rule + fallback) server-side; the client just shows
  // promo[0], never picks or hardcodes a specific code itself.
  const promo = data[0];

  const ctaHref = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('coupon', promo.code);
    const qs = params.toString();
    return `${pathname}${qs ? `?${qs}` : ''}`;
  };

  const handleClaim = (event) => {
    try {
      trackClick(promo?.code, placement, 'goat-spins');
    } catch {
      // tracking must never block the claim
    }

    if (!promo?.code) return; // no coupon code → fall back to normal <a> navigation
    event.preventDefault();
    // Already on "/" — update the URL in place so CouponCashier (reactive
    // useSearchParams, see PromoLandingPage) opens signup/login or the
    // cashier immediately, no extra navigation.
    router.replace(ctaHref(), { scroll: false });
  };

  return (
    <section className={styles.strip} aria-label="Current promotion">
      <div className={styles.card}>
        {promo.content?.badge && (
          <span className={styles.badge}>{promo.content.badge}</span>
        )}
        <div className={styles.copy}>
          {promo.content?.headline && (
            <h2 className={styles.headline}>{promo.content.headline}</h2>
          )}
          {promo.details?.termsText && (
            <p className={styles.terms}>{promo.details.termsText}</p>
          )}
        </div>
        <a
          href={promo.code ? ctaHref() : promo.actions?.redirectUrl || '/'}
          onClick={handleClaim}
          className={styles.cta}
        >
          {promo.content?.ctaText || 'Claim Now'}
        </a>
      </div>
    </section>
  );
}
